import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FlexContainer } from 'components/local/styles/StyledComponents';
import { OpaqueWrapper } from 'components/local/modals/styles/StyledComponents';
import desktopAndLaptop from './assets/desktopAndLaptop.svg';
import getIsChrome from 'utils/libs/getIsChrome';

const MobileWarning = () => {
  const { t } = useTranslation();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isChromeIncompatible, setIsChromeIncompatible] = useState(false);

  const checkChromeVersion = useCallback(() => {
    const isChrome = getIsChrome();

    if (isChrome) {
      // Extract Chrome version from user agent
      const chromeVersionMatch = navigator.userAgent.match(/Chrome\/(\d+)/);
      if (chromeVersionMatch) {
        const chromeVersion = parseInt(chromeVersionMatch[1], 10);

        // Chrome 60+ is required for:
        // - WebRTC getUserMedia API (Chrome 47+)
        // - Canvas API (Chrome 4+)
        // - WebM codec support (Chrome 25+)
        // - AudioContext API (Chrome 14+)
        // - RecordRTC compatibility (Chrome 49+ recommended)
        // - message event (Chrome 60+)
        // - Navigator: mediaDevices property (Chrome 73+)

        const MIN_CHROME_VERSION = 74;

        if (chromeVersion < MIN_CHROME_VERSION) {
          setIsChromeIncompatible(true);
          return;
        }
      }
    }

    setIsChromeIncompatible(false);
  }, []);

  const checkIsSmallScreen = useCallback(() => {
    const isSmallScreen = window.innerWidth <= 960;
    setIsSmallScreen(isSmallScreen);
  }, []);

  const checkIsMobile = useCallback(() => {
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    const isIPad =
      /iPad/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    setIsMobile(isMobileDevice || isIPad);
  }, []);

  useEffect(() => {
    checkIsSmallScreen();
    checkIsMobile();
    checkChromeVersion();

    // Check screen size on resize
    window.addEventListener('resize', checkIsSmallScreen);

    return () => window.removeEventListener('resize', checkIsSmallScreen);
  }, [checkIsSmallScreen, checkIsMobile, checkChromeVersion]);

  if (!isSmallScreen && !isMobile && !isChromeIncompatible) {
    return null;
  }

  let reason = t('mobileWarning.screenTooSmallMessage');
  let reasonTitle = t('mobileWarning.screenTooSmallTitle');
  if (isMobile) {
    reason = t('mobileWarning.desktopOrLaptopNeededMessage');
    reasonTitle = t('mobileWarning.desktopOrLaptopNeededTitle');
  } else if (isChromeIncompatible) {
    reason = t('mobileWarning.browserUpdateRequiredMessage');
    reasonTitle = t('mobileWarning.browserUpdateRequiredTitle');
  }

  return (
    <Overlay>
      <WarningBody
        alignItems="center"
        direction="column"
        justifyContent="center"
      >
        <img src={desktopAndLaptop} alt="Desktop and Laptop Icon" />
        <Warning>{reasonTitle}</Warning>
        {reason}
      </WarningBody>
    </Overlay>
  );
};

export default MobileWarning;

const Overlay = OpaqueWrapper;

const WarningBody = styled(FlexContainer)`
  background: ${({ theme }) => theme.white};
  font-family: ${({ theme }) => theme.fontFamily};
  padding: 48px 24px;
  position: relative;
  text-align: center;
  max-width: 600px;
  font-size: 16px;
  line-height: 24px;
  border-radius: 16px;
`;

const Warning = styled.div`
  font-family: ${({ theme }) => theme.boldFontFamily};
  letter-spacing: 0.44px;
  line-height: 22px;
  margin-bottom: 12px;
  margin-top: 32px;
  line-height: 34px;
  font-size: 22px;
  line-height: 34px;
`;
